import "./SwipeableModals.scss";
import React, { useState, useEffect } from "react";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import {
  Autocomplete,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  CircularProgress,
  Chip,
  Box,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import AddPhoto from "./../../../assets/add-photo.png";
import FileIcon from "@mui/icons-material/FileUpload";
import BlueButton from "../../../components/BlueButton/BlueButton";
import { countriesInArabic, photosApi } from "../../../SourceData";
import api from "../../../utils/api";
import { message } from "antd";

const SwipeableModals = ({
  open,
  onClose,
  onOpen,
  currentView,
  worker,
  onSuccess,
}) => {
  // State for form data
  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    alternativePhone: "",
    salary: "",
    country: "",
    password: "",
    confirmPassword: "",
    isActive: false,
    category: null,
    areas: [],
    governorates: [],
  });

  // State for UI control
  const [showPassword, setShowPassword] = useState(false);
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // State for front and back ID sides instead of idDocument
  const [idFrontSide, setIdFrontSide] = useState(null);
  const [idBackSide, setIdBackSide] = useState(null);
  const [idFrontPreview, setIdFrontPreview] = useState(null);
  const [idBackPreview, setIdBackPreview] = useState(null);

  // New state for categories
  const [categories, setCategories] = useState([]);

  // State for areas and governorates
  const [areasData, setAreasData] = useState([]);
  const [governoratesData, setGovernoratesData] = useState([]);
  const [areasLoading, setAreasLoading] = useState(false);

  // Process and organize data from API
  const processGovernoratesData = (data) => {
    const areasMap = new Map();
    const governorates = [];

    data.forEach((item) => {
      // Add area to map if not exists
      if (item.area && !areasMap.has(item.area._id)) {
        areasMap.set(item.area._id, {
          _id: item.area._id,
          name: item.area.name,
          areaId: item.area.areaId,
        });
      }

      // Add governorate - ensure name is properly structured
      governorates.push({
        _id: item._id,
        governorateId: item.governorateId,
        name: typeof item.name === 'object' ? item.name : { ar: item.name, en: item.name },
        areaId: item.area._id,
        areaName: typeof item.area.name === 'object' ? item.area.name : { ar: item.area.name, en: item.area.name },
      });
    });

    return {
      areas: Array.from(areasMap.values()),
      governorates,
    };
  };

  // Get available governorates based on selected areas
  const getAvailableGovernorates = () => {
    if (!formData.areas || formData.areas.length === 0) {
      return [];
    }

    return governoratesData.filter((gov) =>
      formData.areas.includes(gov.areaId)
    );
  };

  // Function to fetch areas and governorates
  const fetchAreasAndGovernorates = async () => {
    try {
      setAreasLoading(true);
      const response = await api.get("/admin/governorates");

      if (response.data.status === "success" && response.data.data) {
        const { areas, governorates } = processGovernoratesData(
          response.data.data
        );
        setAreasData(areas);
        setGovernoratesData(governorates);
      }
    } catch (error) {
      console.error("Error fetching areas and governorates:", error);
      message.error("حدث خطأ أثناء تحميل المناطق والمحافظات");
    } finally {
      setAreasLoading(false);
    }
  };

  // Fetch categories when component mounts
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get("/categories");
        if (response.data.status === "success") {
          setCategories(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        message.error("حدث خطأ أثناء جلب التصنيفات");
      }
    };

    fetchCategories();
    fetchAreasAndGovernorates();
  }, []);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  // Fill form data when editing an existing worker
  useEffect(() => {
    if (worker && currentView === "editUser") {
      // Extract area IDs from worker.areas array (which contains full objects)
      const areaIds = worker.areas 
        ? worker.areas.map(area => typeof area === 'object' ? area._id : area)
        : [];

      // Extract governorate IDs from worker.governorates array (which contains full objects)
      const governorateIds = worker.governorates 
        ? worker.governorates.map(gov => typeof gov === 'object' ? gov._id : gov)
        : [];

      setFormData({
        name: worker.name || "",
        phoneNumber: worker.phoneNumber || "",
        alternativePhone: worker.alternativePhone || "",
        salary: worker.salary || "",
        country: worker.country || "",
        password: "",
        confirmPassword: "",
        isActive: worker.isActive || false,
        category: worker.category || null,
        areas: areaIds,
        governorates: governorateIds,
      });

      if (worker.photo) {
        // If photo is a URL, we need to handle differently than a file
        setImage(photosApi + worker?.photo);
      }

      // Set ID document previews if they exist
      if (worker.idFrontSide) {
        setIdFrontPreview({
          id: "front",
          url: photosApi + worker?.idFrontSide?.path,
          isExisting: true,
        });
      }

      if (worker.idBackSide) {
        setIdBackPreview({
          id: "back",
          url: photosApi + worker?.idBackSide?.path,
          isExisting: true,
        });
      }
    } else if (worker && currentView === "changePassword") {
      // Only set worker ID for change password view
      // Clear all other fields
      setFormData({
        name: "",
        phoneNumber: "",
        alternativePhone: "",
        salary: "",
        country: "",
        password: "",
        confirmPassword: "",
        isActive: false,
        category: null,
        areas: [],
        governorates: [],
      });
    }
  }, [worker, currentView, open]);

  const resetForm = () => {
    setFormData({
      name: "",
      phoneNumber: "",
      alternativePhone: "",
      salary: "",
      country: "",
      password: "",
      confirmPassword: "",
      isActive: false,
      category: null,
      areas: [],
      governorates: [],
    });
    setImage(null);
    setIdFrontSide(null);
    setIdBackSide(null);
    setIdFrontPreview(null);
    setIdBackPreview(null);
    setErrors({});
    setShowPassword(false);
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  const handleCountryChange = (event, newValue) => {
    setFormData({
      ...formData,
      country: newValue,
    });

    if (errors.country) {
      setErrors({
        ...errors,
        country: null,
      });
    }
  };

  const handleCategoryChange = (event) => {
    const selectedCategoryId = event.target.value;

    // Find the corresponding category object
    const selectedCategory = categories.find(
      (category) => category._id === selectedCategoryId
    );

    setFormData((prev) => ({
      ...prev,
      category: selectedCategory || null,
    }));
  };

  // Handle areas change
  const handleAreasChange = (event) => {
    const {
      target: { value },
    } = event;

    const newAreas = typeof value === "string" ? value.split(",") : value;

    setFormData((prev) => {
      // Filter out governorates that belong to removed areas
      const removedAreas = prev.areas.filter(
        (areaId) => !newAreas.includes(areaId)
      );

      let updatedGovernorates = [...prev.governorates];

      if (removedAreas.length > 0) {
        // Remove governorates that belong to removed areas
        updatedGovernorates = updatedGovernorates.filter((govId) => {
          const gov = governoratesData.find((g) => g._id === govId);
          return gov && !removedAreas.includes(gov.areaId);
        });
      }

      return {
        ...prev,
        areas: newAreas,
        governorates: updatedGovernorates,
      };
    });

    // Clear error when user starts selecting
    if (errors.areas) {
      setErrors({
        ...errors,
        areas: null,
      });
    }
  };

  // Handle governorates change
  const handleGovernoratesChange = (event) => {
    const {
      target: { value },
    } = event;

    const newGovernorates =
      typeof value === "string" ? value.split(",") : value;

    setFormData((prev) => ({
      ...prev,
      governorates: newGovernorates,
    }));

    // Clear error when user starts selecting
    if (errors.governorates) {
      setErrors({
        ...errors,
        governorates: null,
      });
    }
  };

  // Get area name by ID
  const getAreaName = (areaId) => {
    const area = areasData.find((a) => a._id === areaId);
    if (!area) return areaId;
    
    // Safely access the Arabic name
    return typeof area.name === 'object' ? area.name.ar : area.name;
  };

  // Get governorate name by ID
  const getGovernorateName = (govId) => {
    const gov = governoratesData.find((g) => g._id === govId);
    if (!gov) return govId;
    
    // Safely access the Arabic name
    return typeof gov.name === 'object' ? gov.name.ar : gov.name;
  };

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setImage(e.target.files[0]);

      if (errors.photo) {
        setErrors({
          ...errors,
          photo: null,
        });
      }
    }
  };

  // Handlers for front and back ID sides
  const handleIdFrontSideChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setIdFrontSide(file);

      // Create preview URL for the file
      setIdFrontPreview({
        id: "front",
        url: URL.createObjectURL(file),
        file: file,
        isExisting: false,
      });

      if (errors.idFrontSide) {
        setErrors({
          ...errors,
          idFrontSide: null,
        });
      }
    }
  };

  const handleIdBackSideChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setIdBackSide(file);

      // Create preview URL for the file
      setIdBackPreview({
        id: "back",
        url: URL.createObjectURL(file),
        file: file,
        isExisting: false,
      });

      if (errors.idBackSide) {
        setErrors({
          ...errors,
          idBackSide: null,
        });
      }
    }
  };

  const handleRemovePhoto = () => {
    setImage(null);
  };

  // Handlers for removing front and back sides
  const handleRemoveFrontSide = () => {
    setIdFrontSide(null);
    setIdFrontPreview(null);
  };

  const handleRemoveBackSide = () => {
    setIdBackSide(null);
    setIdBackPreview(null);
  };

  const validateForm = () => {
    const newErrors = {};

    if (currentView === "changePassword") {
      // Only validate password fields for password change
      if (!formData.password || formData.password.length < 8) {
        newErrors.password = "كلمة المرور مطلوبة وتتكون من 8 أحرف على الأقل";
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = "كلمة المرور غير متطابقة";
      }
    } else {
      // Required fields validation for create/edit
      if (!formData.name || formData.name.length < 3) {
        newErrors.name = "الاسم مطلوب ويجب أن يكون على الأقل 3 أحرف";
      }

      // Updated phone validation for format: 0506070800
      if (!formData.phoneNumber) {
        newErrors.phoneNumber = "رقم الهاتف مطلوب";
      } else if (!/^05\d{8}$/.test(formData.phoneNumber)) {
        newErrors.phoneNumber = "يرجى إدخال رقم هاتف صحيح بتنسيق 05xxxxxxxx";
      }

      if (!formData.country) {
        newErrors.country = "الجنسية مطلوبة";
      }

      // Category validation
      if (!formData.category) {
        newErrors.category = "التصنيف مطلوب";
      }

      // Areas validation
      if (!formData.areas || formData.areas.length === 0) {
        newErrors.areas = "يرجى اختيار منطقة واحدة على الأقل";
      }

      // Governorates validation
      if (!formData.governorates || formData.governorates.length === 0) {
        newErrors.governorates = "يرجى اختيار محافظة واحدة على الأقل";
      }

      // Password validation only for new user
      if (currentView === "createShop") {
        if (!formData.password || formData.password.length < 8) {
          newErrors.password = "كلمة المرور مطلوبة وتتكون من 8 أحرف على الأقل";
        }

        if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = "كلمة المرور غير متطابقة";
        }

        if (!image) {
          newErrors.photo = "الصورة مطلوبة";
        }

        // Check for front and back ID sides
        if (!idFrontSide) {
          newErrors.idFrontSide = "صورة وجه الهوية مطلوبة";
        }

        if (!idBackSide) {
          newErrors.idBackSide = "صورة ظهر الهوية مطلوبة";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Handle password change separately
      if (currentView === "changePassword" && worker) {
        const passwordData = {
          password: formData.password,
          confirmPassword: formData.confirmPassword,
        };

        const response = await api.patch(
          `/admin/technician/${worker._id}/updatePassword`,
          passwordData
        );

        if (response.data.status === "success") {
          message.success("تم تغيير كلمة المرور بنجاح");
          resetForm();
          onClose();
        }
      } else {
        const formDataToSend = new FormData();
        formDataToSend.append("name", formData.name);
        formDataToSend.append("phoneNumber", formData.phoneNumber);

        if (formData.alternativePhone) {
          formDataToSend.append("alternativePhone", formData.alternativePhone);
        }

        if (formData.salary) {
          formDataToSend.append("salary", formData.salary);
        }

        if (formData.country) {
          formDataToSend.append("country", formData.country);
        }

        // Add category
        if (formData.category) {
          formDataToSend.append("category", formData.category._id);
        }

        // Add areas and governorates
        if (formData.areas && formData.areas.length > 0) {
          formData.areas.forEach((areaId) => {
            formDataToSend.append("areas[]", areaId);
          });
        }

        if (formData.governorates && formData.governorates.length > 0) {
          formData.governorates.forEach((govId) => {
            formDataToSend.append("governorates[]", govId);
          });
        }

        // Add password only for new users (createShop)
        if (currentView === "createShop" && formData.password) {
          formDataToSend.append("password", formData.password);
        }

        // Handle photo/image field
        if (image instanceof File) {
          // If a new file is uploaded, send it
          formDataToSend.append("photo", image);
        } else if (currentView === "editUser") {
          // For edit mode, if image is null, explicitly send null
          // (this indicates user wants to remove the image)
          if (!image) {
            formDataToSend.append("photo", "null");
          }
          // If image is a string (URL from server), don't send anything
          // as this means user didn't change the image
        }

        // Handle front side ID
        if (idFrontSide instanceof File) {
          // If a new file is uploaded, send it
          formDataToSend.append("idFrontSide", idFrontSide);
        } else if (currentView === "editUser") {
          // For edit mode, if idFrontPreview is null, explicitly send null
          // (this indicates user wants to remove the front side)
          if (!idFrontPreview) {
            formDataToSend.append("idFrontSide", "null");
          }
          // If idFrontPreview exists and is from server, don't send anything
        }

        // Handle back side ID
        if (idBackSide instanceof File) {
          // If a new file is uploaded, send it
          formDataToSend.append("idBackSide", idBackSide);
        } else if (currentView === "editUser") {
          // For edit mode, if idBackPreview is null, explicitly send null
          // (this indicates user wants to remove the back side)
          if (!idBackPreview) {
            formDataToSend.append("idBackSide", "null");
          }
          // If idBackPreview exists and is from server, don't send anything
        }

        let response;

        if (currentView === "createShop") {
          // Create new worker
          response = await api.post("/admin/technician", formDataToSend, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
          message.success("تم إضافة العامل بنجاح");
        } else if (currentView === "editUser" && worker) {
          // Update existing worker
          response = await api.patch(
            `/admin/technician/${worker._id}/updateData`,
            formDataToSend,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );
          message.success("تم تحديث بيانات العامل بنجاح");
        }

        if (response && response.data.status === "success") {
          resetForm();
          onSuccess(); // Refresh workers list
          onClose(); // Close modal
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);

      // Handle different error types
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        message.error(error.response.data.message);
      } else {
        message.error("حدث خطأ أثناء حفظ البيانات");
      }
    } finally {
      setLoading(false);
    }
  };

  const renderPasswordChangeForm = () => (
    <div className="user-modal">
      <p className="user-modal-p">تغيير كلمة المرور</p>
      <form onSubmit={handleSubmit}>
        {/*كلمة المرور*/}
        <FormControl
          sx={{ width: "100%", borderRadius: "8px" }}
          variant="outlined"
          required
        >
          <InputLabel
            htmlFor="password"
            sx={{
              left: "auto",
              background: "white",
              right: 12,
              transformOrigin: "right",
              "&:not(.MuiInputLabel-shrink)": {
                transform: "translate(-8px, 12px) scale(1)",
              },
              "&.MuiInputLabel-shrink": {
                transform: "translate(0, -10px) scale(0.8)",
              },
            }}
          >
            كلمة المرور الجديدة
          </InputLabel>
          <OutlinedInput
            required
            sx={{ borderRadius: "8px" }}
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={handleInputChange}
            error={!!errors.password}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
          {errors.password && (
            <p className="error-message">{errors.password}</p>
          )}
        </FormControl>

        {/*تاكيد كلمة المرور*/}
        <FormControl sx={{ width: "100%" }} variant="outlined" required>
          <InputLabel
            htmlFor="confirmPassword"
            sx={{
              left: "auto",
              background: "white",
              right: 12,
              transformOrigin: "right",
              "&:not(.MuiInputLabel-shrink)": {
                transform: "translate(-8px, 12px) scale(1)",
              },
              "&.MuiInputLabel-shrink": {
                transform: "translate(0, -10px) scale(0.8)",
              },
            }}
          >
            تاكيد كلمة المرور الجديدة
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="confirmPassword"
            name="confirmPassword"
            type={showPassword ? "text" : "password"}
            value={formData.confirmPassword}
            onChange={handleInputChange}
            error={!!errors.confirmPassword}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
          {errors.confirmPassword && (
            <p className="error-message">{errors.confirmPassword}</p>
          )}
        </FormControl>

        <BlueButton
          text="تحديث كلمة المرور"
          size="large"
          variant="fill"
          type="submit"
          disabled={loading}
          icon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        />
      </form>
    </div>
  );

  const renderWorkerForm = () => (
    <div className="user-modal">
      <p className="user-modal-p">
        {currentView === "createShop"
          ? "اضافة عامل جديد"
          : "تعديل بيانات العامل"}
      </p>
      <form onSubmit={handleSubmit}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            position: "relative",
            marginBottom: "16px",
          }}
        >
          <div style={{ position: "relative" }}>
            <label
              htmlFor="image"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <img
                src={
                  image instanceof File
                    ? URL.createObjectURL(image)
                    : typeof image === "string"
                    ? image // URL from API
                    : AddPhoto
                }
                alt=""
                style={{
                  width: "100px",
                  height: "100px",
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "1px dashed #ccc",
                }}
              />
              <span
                style={{
                  marginTop: "8px",
                  color: "#1976d2",
                  fontSize: "14px",
                }}
              >
                اضافة صورة
              </span>
            </label>

            {image && (
              <div
                onClick={handleRemovePhoto}
                style={{
                  position: "absolute",
                  top: "-5px",
                  right: "-5px",
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  backgroundColor: "#f44336",
                  color: "white",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  fontSize: "18px",
                  fontWeight: "bold",
                }}
              >
                ×
              </div>
            )}
          </div>

          <input
            type="file"
            id="image"
            onChange={handleImageChange}
            accept="image/*"
            style={{ display: "none" }}
          />
          {errors.photo && (
            <p
              style={{
                color: "#d32f2f",
                fontSize: "12px",
                margin: "4px 0 0 0",
              }}
            >
              {errors.photo}
            </p>
          )}
        </div>

        {/* الاسم */}
        <FormControl sx={{ width: "100%" }} variant="outlined" required>
          <InputLabel
            htmlFor="name"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            اسم العامل
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleInputChange}
            notched
            error={!!errors.name}
          />
          {errors.name && <p className="error-message">{errors.name}</p>}
        </FormControl>

        {/* رقم الموبايل */}
        <FormControl sx={{ width: "100%" }} variant="outlined" required>
          <InputLabel
            htmlFor="phoneNumber"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            رقم الهاتف
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="phoneNumber"
            name="phoneNumber"
            type="text"
            value={formData.phoneNumber}
            onChange={handleInputChange}
            placeholder="05xxxxxxxx"
            notched
            error={!!errors.phoneNumber}
          />
          {errors.phoneNumber && (
            <p className="error-message">{errors.phoneNumber}</p>
          )}
        </FormControl>

        {/* رقم هاتف اخر */}
        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="alternativePhone"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            رقم هاتف اخر
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            id="alternativePhone"
            name="alternativePhone"
            type="text"
            value={formData.alternativePhone}
            onChange={handleInputChange}
            placeholder="05xxxxxxxx"
            notched
          />
        </FormControl>

        {/* الراتب */}
        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="salary"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            الراتب
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            placeholder="ريال"
            id="salary"
            name="salary"
            type="number"
            value={formData.salary}
            onChange={handleInputChange}
            notched
          />
        </FormControl>

        {/* جنسية العامل */}
        <Autocomplete
          id="country-select"
          sx={{ width: "100%" }}
          options={countriesInArabic}
          value={formData.country}
          onChange={handleCountryChange}
          renderInput={(params) => (
            <TextField
              {...params}
              label="جنسية العامل"
              placeholder="جنسية العامل"
              error={!!errors.country}
              InputLabelProps={{
                shrink: true,
                sx: {
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.75)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                },
              }}
            />
          )}
          renderOption={(props, option) => (
            <MenuItem {...props}>{option}</MenuItem>
          )}
        />
        {errors.country && <p className="error-message">{errors.country}</p>}

        {/* القسم */}
        <FormControl fullWidth>
          <InputLabel
            htmlFor="category-select"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            القسم
          </InputLabel>
          <Select
            labelId="category-select-label"
            id="category-select"
            displayEmpty={true}
            sx={{ borderRadius: "8px" }}
            value={formData.category?._id || ""}
            onChange={handleCategoryChange}
          >
            <MenuItem value="">
              <em>اختر القسم</em>
            </MenuItem>

            {categories && categories.length > 0 ? (
              categories.map((category) => (
                <MenuItem key={category._id} value={category._id}>
                  {category?.name?.ar || ""}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>لا توجد أقسام متاحة</MenuItem>
            )}
          </Select>
        </FormControl>
        {errors.category && <p className="error-message">{errors.category}</p>}

        {/* Areas Multi-Select */}
        <FormControl fullWidth required>
          <InputLabel
            htmlFor="areas-select"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            المناطق التي يعمل بها
          </InputLabel>
          <Select
            labelId="areas-select-label"
            id="areas-select"
            multiple
            displayEmpty
            sx={{ borderRadius: "8px" }}
            value={formData.areas}
            onChange={handleAreasChange}
            disabled={areasLoading}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <em>اختر المناطق</em>;
              }
              return (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip
                      key={value}
                      label={getAreaName(value)}
                      size="small"
                    />
                  ))}
                </Box>
              );
            }}
            required
          >
            <MenuItem disabled value="">
              <em>{areasLoading ? "جاري التحميل..." : "اختر المناطق"}</em>
            </MenuItem>
            {areasLoading ? (
              <MenuItem disabled>
                <CircularProgress size={20} /> جاري التحميل...
              </MenuItem>
            ) : (
              areasData.map((area) => (
                <MenuItem key={area._id} value={area._id}>
                  <Checkbox checked={formData.areas.indexOf(area._id) > -1} />
                  <ListItemText primary={typeof area.name === 'object' ? area.name.ar : area.name} />
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
        {errors.areas && <p className="error-message">{errors.areas}</p>}

        {/* Governorates Multi-Select */}
        <FormControl
          fullWidth
          required
          disabled={formData.areas.length === 0 || areasLoading}
        >
          <InputLabel
            htmlFor="governorates-select"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            المحافظات التي يعمل بها
          </InputLabel>
          <Select
            labelId="governorates-select-label"
            id="governorates-select"
            multiple
            displayEmpty
            sx={{ borderRadius: "8px" }}
            value={formData.governorates}
            onChange={handleGovernoratesChange}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <em>اختر المحافظات</em>;
              }
              return (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip
                      key={value}
                      label={getGovernorateName(value)}
                      size="small"
                    />
                  ))}
                </Box>
              );
            }}
            required
          >
            <MenuItem disabled value="">
              <em>
                {formData.areas.length === 0
                  ? "يرجى اختيار المناطق أولاً"
                  : "اختر المحافظات"}
              </em>
            </MenuItem>
            {getAvailableGovernorates().map((gov) => (
              <MenuItem key={gov._id} value={gov._id}>
                <Checkbox
                  checked={formData.governorates.indexOf(gov._id) > -1}
                />
                <ListItemText
                  primary={typeof gov.name === 'object' ? gov.name.ar : gov.name}
                  secondary={typeof gov.areaName === 'object' ? `(${gov.areaName.ar})` : `(${gov.areaName})`}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {errors.governorates && (
          <p className="error-message">{errors.governorates}</p>
        )}

        {/* Show password fields only for createShop (new worker) */}
        {currentView === "createShop" && (
          <>
            {/*كلمة المرور*/}
            <FormControl
              sx={{ width: "100%", borderRadius: "8px" }}
              variant="outlined"
              required
            >
              <InputLabel
                htmlFor="password"
                sx={{
                  left: "auto",
                  background: "white",
                  right: 12,
                  transformOrigin: "right",
                  "&:not(.MuiInputLabel-shrink)": {
                    transform: "translate(-8px, 12px) scale(1)",
                  },
                  "&.MuiInputLabel-shrink": {
                    transform: "translate(0, -10px) scale(0.8)",
                  },
                }}
              >
                كلمة المرور
              </InputLabel>
              <OutlinedInput
                required
                sx={{ borderRadius: "8px" }}
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleInputChange}
                error={!!errors.password}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              {errors.password && (
                <p className="error-message">{errors.password}</p>
              )}
            </FormControl>

            {/*تاكيد كلمة المرور*/}
            <FormControl sx={{ width: "100%" }} variant="outlined" required>
              <InputLabel
                htmlFor="confirmPassword"
                sx={{
                  left: "auto",
                  background: "white",
                  right: 12,
                  transformOrigin: "right",
                  "&:not(.MuiInputLabel-shrink)": {
                    transform: "translate(-8px, 12px) scale(1)",
                  },
                  "&.MuiInputLabel-shrink": {
                    transform: "translate(0, -10px) scale(0.8)",
                  },
                }}
              >
                تاكيد كلمة المرور
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                required
                id="confirmPassword"
                name="confirmPassword"
                type={showPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={handleInputChange}
                error={!!errors.confirmPassword}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              {errors.confirmPassword && (
                <p className="error-message">{errors.confirmPassword}</p>
              )}
            </FormControl>
          </>
        )}

        {/* صور البطاقة - Separate Front and Back sides */}
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            marginBottom: "16px",
          }}
        >
          {/* Front Side ID */}
          <div>
            <label
              htmlFor="idFrontSide"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 16px",
                border: "1px dashed #ccc",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              <span>صورة وجه الهوية</span>
              <FileIcon style={{ color: "#1976d2" }} />
            </label>
            <input
              type="file"
              id="idFrontSide"
              onChange={handleIdFrontSideChange}
              accept="image/*"
              style={{ display: "none" }}
            />

            {/* Front ID Preview */}
            {idFrontPreview && (
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  marginTop: "10px",
                }}
              >
                <div
                  style={{
                    position: "relative",
                    width: "80px",
                    height: "80px",
                    borderRadius: "4px",
                    overflow: "hidden",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <img
                    src={idFrontPreview.url}
                    alt="Front Side ID"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <div
                    onClick={handleRemoveFrontSide}
                    style={{
                      position: "absolute",
                      top: "2px",
                      right: "2px",
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      backgroundColor: "rgba(244, 67, 54, 0.9)",
                      color: "white",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "bold",
                    }}
                  >
                    ×
                  </div>
                </div>
              </div>
            )}

            {errors.idFrontSide && (
              <p
                style={{
                  color: "#d32f2f",
                  fontSize: "12px",
                  margin: "4px 0 0 0",
                }}
              >
                {errors.idFrontSide}
              </p>
            )}
          </div>

          {/* Back Side ID */}
          <div>
            <label
              htmlFor="idBackSide"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 16px",
                border: "1px dashed #ccc",
                borderRadius: "8px",
                cursor: "pointer",
                marginTop: "10px",
              }}
            >
              <span>صورة ظهر الهوية</span>
              <FileIcon style={{ color: "#1976d2" }} />
            </label>
            <input
              type="file"
              id="idBackSide"
              onChange={handleIdBackSideChange}
              accept="image/*"
              style={{ display: "none" }}
            />

            {/* Back ID Preview */}
            {idBackPreview && (
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  marginTop: "10px",
                }}
              >
                <div
                  style={{
                    position: "relative",
                    width: "80px",
                    height: "80px",
                    borderRadius: "4px",
                    overflow: "hidden",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <img
                    src={idBackPreview.url}
                    alt="Back Side ID"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <div
                    onClick={handleRemoveBackSide}
                    style={{
                      position: "absolute",
                      top: "2px",
                      right: "2px",
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      backgroundColor: "rgba(244, 67, 54, 0.9)",
                      color: "white",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "bold",
                    }}
                  >
                    ×
                  </div>
                </div>
              </div>
            )}

            {errors.idBackSide && (
              <p
                style={{
                  color: "#d32f2f",
                  fontSize: "12px",
                  margin: "4px 0 0 0",
                }}
              >
                {errors.idBackSide}
              </p>
            )}
          </div>
        </div>

        <BlueButton
          text={currentView === "createShop" ? "اضافة" : "تحديث"}
          size="medium"
          variant="fill"
          type="submit"
          disabled={loading || areasLoading}
          icon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        />
      </form>
    </div>
  );

  const renderDefaultView = () => <div></div>;

  const views = {
    createShop: renderWorkerForm,
    editUser: renderWorkerForm,
    changePassword: renderPasswordChangeForm,
  };

  return (
    <SwipeableDrawer
      anchor="left"
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      sx={{ zIndex: "1202" }}
      disableScrollLock
    >
      {(views[currentView] || renderDefaultView)()}
    </SwipeableDrawer>
  );
};

export default SwipeableModals;